import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../lib/api'

/** SIGNED OUT ONLY AFTER A LONG TIME WITH NOBODY AT THE SCREEN (075).
 *
 *  The customer, 10 Oct 2026: it "gets logged out after a certain amount of
 *  time, even in the middle of working in it". A sign-in lasted a fixed
 *  twelve hours from signing in. Now every REAL use of the screen -- a key, a
 *  click, a tap, the mouse -- moves its end to the organization's idle time
 *  from now (Settings, Operations), told to the server at most once a minute
 *  (`/api/session/touch`). The screen's own refreshing never counts, so an
 *  empty desk still signs out in the end.
 *
 *  FIVE MINUTES BEFORE, A WARNING with "Stay signed in". When the sign-in has
 *  ended the screen goes to the sign-in page instead of failing every press
 *  in silence, which is what it did. A call being typed is a draft (069):
 *  nothing is lost either way. Classes carry `ss-`. */

const TICK = 15_000
const TOUCH_EVERY = 60_000
const WARN = 5 * 60_000

export function StaySignedIn({ onSignedOut }: { onSignedOut: () => void }) {
  const [ends, setEnds] = useState<number | null>(null)
  /** The same, for the timer: it is started ONCE, never again per answer. */
  const endsRef = useRef<number | null>(null)
  endsRef.current = ends
  const [now, setNow] = useState(() => Date.now())
  const lastInput = useRef(0)
  const lastTouch = useRef(Date.now())
  const lastAsked = useRef(0)
  const gone = useRef(false)

  const out = () => { if (!gone.current) { gone.current = true; onSignedOut() } }
  const ended = (e: unknown) => e instanceof ApiError && e.status === 401

  /** The end on THIS screen's clock: the server says how long is left by
   *  its own clock, so a PC whose clock is wrong still counts right. */
  const local = (r: { expires_at: string; now?: string }) =>
    Date.now() + (Date.parse(r.expires_at) - (r.now ? Date.parse(r.now) : Date.now()))

  async function touch() {
    lastTouch.current = Date.now()
    try {
      const r = await api.sessionTouch()
      setEnds(local(r))
    } catch (e) {
      if (ended(e)) out()
    }
  }

  async function ask() {
    lastAsked.current = Date.now()
    try {
      const r = await api.sessionExpiry()
      if (r.expires_at) setEnds(local({ expires_at: r.expires_at, now: r.now }))
    } catch (e) {
      if (ended(e)) out()
    }
  }

  useEffect(() => {
    const mark = () => { lastInput.current = Date.now() }
    // A MOUSE THAT REALLY MOVED: the browser also fires one, standing still,
    // when the page scrolls under a parked pointer -- a new text arriving.
    const moved = (e: MouseEvent) => { if (e.movementX || e.movementY) mark() }
    // THE RADIO PANEL IS ANOTHER SITE IN A FRAME, so its clicks and keys never
    // reach this window: going into it (this window loses focus to the frame)
    // and keying up on it (`crm-use`, from PanelHost) count as using it.
    const intoFrame = () => { if (document.activeElement?.tagName === 'IFRAME') mark() }
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'crm-use'] as const
    events.forEach((ev) => window.addEventListener(ev, mark, { passive: true }))
    window.addEventListener('mousemove', moved, { passive: true })
    window.addEventListener('blur', intoFrame)
    ask()
    const t = setInterval(() => {
      const at = Date.now()
      setNow(at)
      // SOMEBODY IS HERE: say so, at most once a minute.
      if (lastInput.current > lastTouch.current && at - lastTouch.current >= TOUCH_EVERY) {
        touch()
        return
      }
      // NOBODY, AND THE END IS NEAR: ask again -- another tab of this browser
      // may have moved it -- and when it has passed, the server says so.
      const end = endsRef.current
      if (end !== null && end - at < 2 * WARN && at - lastAsked.current >= TOUCH_EVERY) ask()
    }, TICK)
    return () => {
      clearInterval(t)
      events.forEach((ev) => window.removeEventListener(ev, mark))
      window.removeEventListener('mousemove', moved)
      window.removeEventListener('blur', intoFrame)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (ends === null || ends - now > WARN) return null
  const minutes = Math.max(1, Math.ceil((ends - now) / 60_000))
  return (
    <div className="ss-warn" role="alert">
      Nobody has used this screen for a while — it signs out in {minutes} minute{minutes === 1 ? '' : 's'}.
      <button type="button" className="btn sm" onClick={() => { touch(); setNow(Date.now()) }}>Stay signed in</button>
    </div>
  )
}
